import React, { useState } from "react";
import { avanzoApi } from "@/lib/avanzoApi";

/**
 * BuddyReflectionCard — Fase 8.1
 * Micro-momento de acompañamiento (0 LLM por defecto).
 * Flujo: observation → (question opcional) → cierre + "¿Fue útil?"
 * El estudiante puede omitir en cualquier momento.
 *
 * Props:
 *   reflection: { reflection_id, reflection_type, observation, question, closing, generative_used }
 *   onClose:    () => void          — cierre inmediato (omitir / continuar)
 *   variant:    "training" | "post"  — training se ancla al card, post es inline
 *
 * Reglas:
 *   - Nunca bloquea un reactivo en curso (padre controla cuándo renderizarlo).
 *   - No persiste la respuesta abierta como transcript (backend solo guarda señal length).
 *   - No expone contenido de evaluaciones (backend sanitiza).
 */
const MAX_CHARS = 300;

export default function BuddyReflectionCard({ reflection, onClose, variant = "training" }) {
  const [step, setStep] = useState(reflection?.question ? "observation" : "closing");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [helpfulSent, setHelpfulSent] = useState(null);

  if (!reflection) return null;

  const rid = reflection.reflection_id;

  const handleRespond = async () => {
    const clean = (text || "").trim().slice(0, MAX_CHARS);
    setSubmitting(true);
    try {
      if (clean) {
        await avanzoApi.buddyRespond({ reflection_id: rid, text: clean });
      }
      setStep("closing");
    } catch (_) {
      // Silencioso: la reflexión no puede bloquear el flujo pedagógico.
      setStep("closing");
    } finally {
      setSubmitting(false);
    }
  };

  const handleHelpful = async (val) => {
    if (helpfulSent !== null) return;
    setHelpfulSent(val);
    try {
      await avanzoApi.buddyHelpful({ reflection_id: rid, helpful: val });
    } catch (_) { /* silencioso */ }
  };

  const wrapperClass = variant === "post"
    ? "avz-card avz-mt-6"
    : "avz-card avz-mt-6";

  return (
    <div
      className={wrapperClass}
      data-testid="avz-buddy-card"
      data-reflection-id={rid}
      data-reflection-type={reflection.reflection_type}
      role="region"
      aria-label="Un momento para reflexionar"
    >
      <div className="avz-row-between" style={{ marginBottom: 4 }}>
        <span className="avz-chip" data-testid="avz-buddy-chip">
          Un momento para pensar
        </span>
        <button
          type="button"
          className="avz-btn avz-btn-ghost avz-btn-sm"
          onClick={onClose}
          data-testid="avz-buddy-skip"
          aria-label="Omitir reflexión"
        >
          Omitir
        </button>
      </div>

      <p
        className="avz-lead avz-mt-4"
        data-testid="avz-buddy-observation"
      >
        {reflection.observation}
      </p>

      {step === "observation" && reflection.question && (
        <div className="avz-mt-4" data-testid="avz-buddy-question-block">
          <p
            className="avz-h3"
            data-testid="avz-buddy-question"
          >
            {reflection.question}
          </p>
          <textarea
            className="avz-input avz-mt-4"
            rows={3}
            maxLength={MAX_CHARS}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Puedes escribir una idea breve (opcional)"
            data-testid="avz-buddy-textarea"
            aria-label="Tu reflexión (opcional)"
          />
          <div className="avz-row-between avz-mt-4">
            <span className="avz-mute" data-testid="avz-buddy-count">
              {text.length}/{MAX_CHARS}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="avz-btn avz-btn-ghost"
                onClick={() => setStep("closing")}
                data-testid="avz-buddy-skip-question"
              >
                Saltar
              </button>
              <button
                type="button"
                className="avz-btn avz-btn-primary"
                onClick={handleRespond}
                disabled={submitting}
                data-testid="avz-buddy-respond"
              >
                {submitting ? "Enviando…" : "Continuar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === "closing" && reflection.closing && (
        <div className="avz-mt-4" data-testid="avz-buddy-closing-block">
          <p
            className="avz-lead"
            data-testid="avz-buddy-closing"
          >
            {reflection.closing}
          </p>

          {helpfulSent === null && (
            <div className="avz-mt-4" data-testid="avz-buddy-helpful-block">
              <span className="avz-mute">¿Fue útil este momento?</span>
              <div className="avz-row avz-mt-4" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="avz-btn avz-btn-ghost"
                  onClick={() => handleHelpful(true)}
                  data-testid="avz-buddy-helpful-yes"
                  aria-label="Sí, fue útil"
                >
                  Sí
                </button>
                <button
                  type="button"
                  className="avz-btn avz-btn-ghost"
                  onClick={() => handleHelpful(false)}
                  data-testid="avz-buddy-helpful-no"
                  aria-label="No, no fue útil"
                >
                  No
                </button>
              </div>
            </div>
          )}

          {helpfulSent !== null && (
            <p className="avz-mute avz-mt-4" data-testid="avz-buddy-helpful-ack">
              Gracias por tu respuesta.
            </p>
          )}

          <div className="avz-row-between avz-mt-4">
            <span className="avz-mute" />
            <button
              type="button"
              className="avz-btn avz-btn-primary"
              onClick={onClose}
              data-testid="avz-buddy-continue"
            >
              Continuar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
